/**
 * Informes.js — Informe analítico unificado de CxC / CxP
 *
 * Página con switch CxC/CxP, filtros avanzados, botones de período rápido y
 * comparación contra el período anterior. Visualización pulida:
 *   1. KPIs con delta ↑/↓ vs período anterior
 *   2. Evolución mensual — área con degradado (facturado vs cobrado/pagado)
 *   3. Distribución por rubro — dona con total al centro y leyenda con %
 *   4. Deuda por entidad — barras horizontales (ranking de deudores)
 *   5. Detalle documento por documento — tabla paginada
 *
 * Montos del backend en COLONES; el toggle ₡/$ convierte con el TC de compra.
 */
import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Sector, LabelList,
} from "recharts";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { useTipoCambio } from "../../services/useTipoCambio";
import { useTheme } from "../../../../shared/contexts/ThemeContext";
import { SearchSelect } from "../../../../shared/components/SearchSelect";
import CurrencyToggle from "../../shared/CurrencyToggle";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

// Paleta de la dona por rubro — 8 hues en orden fijo, validadas (con el
// validador de la skill de dataviz) para que cada color se distinga de sus
// vecinos incluso bajo daltonismo, en ambos temas. Cada rubro conserva SU
// color aunque cambien los filtros. Más allá de 8 rubros, el resto se agrupa
// en "Otros" (gris neutro) — 8 es el máximo de colores distinguibles.
const RUBRO_COLORS = {
  dark:  ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
};
const RUBRO_OTROS = { dark: "#6b7280", light: "#94a3b8" };
const RUBRO_MAX = 8;
const PAGE_SIZE = 15;
const FILTROS_INIT = { entidad: "", rubro: "", fechaInicio: "", fechaFinal: "", estado: "todos", moneda: "todos" };

const MESES_CORTO = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function rangoMes(offset = 0) {
  const n = new Date();
  const first = new Date(n.getFullYear(), n.getMonth() + offset, 1);
  const last = new Date(n.getFullYear(), n.getMonth() + offset + 1, 0);
  return [ymd(first), ymd(last)];
}
function rangoAnio() {
  const y = new Date().getFullYear();
  return [`${y}-01-01`, `${y}-12-31`];
}
function mesLabel(ym) {
  const [y, m] = ym.split("-");
  return `${MESES_CORTO[Number(m) - 1] || m} ${y.slice(2)}`;
}
/** "2026-07-05" → "05 Jul" — etiqueta para evolución diaria (rango ~1 mes). */
function diaLabel(ymd_) {
  const [, m, d] = ymd_.split("-");
  return `${d} ${MESES_CORTO[Number(m) - 1] || m}`;
}
/** Etiqueta de un punto de "periodo" según la granularidad que devolvió el backend. */
function periodoLabel(periodo, granularidad) {
  return granularidad === "diaria" ? diaLabel(periodo) : mesLabel(periodo);
}
function fmtFecha(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function useNarrow(bp = 900) {
  const [n, setN] = useState(() => (typeof window !== "undefined" ? window.innerWidth < bp : false));
  useEffect(() => {
    const h = () => setN(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return n;
}

export default function Informes() {
  const { t } = useTranslation();
  const narrow = useNarrow(900);
  const { theme } = useTheme();
  const rubroColors = RUBRO_COLORS[theme] ?? RUBRO_COLORS.dark;
  const rubroOtrosColor = RUBRO_OTROS[theme] ?? RUBRO_OTROS.dark;
  const multimoneda = useMultimoneda();
  const { tipoCambio } = useTipoCambio();
  const tc = Number(tipoCambio?.tipo_compra) || 1;
  const [searchParams] = useSearchParams();

  const [tipo, setTipo] = useState(searchParams.get("tipo") === "cxp" ? "cxp" : "cxc");
  const [vista, setVista] = useState("CRC");
  const [filtros, setFiltros] = useState({ ...FILTROS_INIT });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rubroActivo, setRubroActivo] = useState(null); // índice de la porción con hover, o null

  const esUSD = vista === "USD";
  const sym = esUSD ? "$" : "₡";
  const conv = (v) => (esUSD ? Number(v || 0) / tc : Number(v || 0));
  const fmt = (v) => `${sym}${esUSD
    ? conv(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : formatCurrency(v)}`;
  // fmtDisp: igual que fmt pero para un valor que YA pasó por conv() (los
  // datos de las gráficas ya vienen convertidos) — evita reconvertir doble.
  const fmtDisp = (v) => `${sym}${esUSD
    ? Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : formatCurrency(v)}`;
  // fmtAxis recibe valores de ejes/gráficas que YA pasaron por conv() — no
  // debe volver a convertir (ese era el mismo bug que fmtDisp evita en los
  // tooltips: dividir dos veces por el TC en modo USD).
  const fmtAxis = (v) => {
    const n = Number(v) || 0; const a = Math.abs(n);
    if (a >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
    if (a >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}K`;
    return `${sym}${n.toFixed(0)}`;
  };

  const esCxc = tipo === "cxc";

  useEffect(() => {
    const qs = new URLSearchParams({ tipo });
    if (filtros.entidad)     qs.set("entidad", filtros.entidad);
    if (filtros.rubro)       qs.set("rubro", filtros.rubro);
    if (filtros.fechaInicio) qs.set("fecha_inicio", filtros.fechaInicio);
    if (filtros.fechaFinal)  qs.set("fecha_final", filtros.fechaFinal);
    if (filtros.estado !== "todos") qs.set("estado", filtros.estado);
    if (filtros.moneda !== "todos") qs.set("moneda", filtros.moneda);
    setLoading(true);
    apiFetch(`/api/finance/informes/?${qs.toString()}`)
      .then((r) => r.json())
      .then((d) => { setData(d); setPage(1); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tipo, filtros]);

  function cambiarTipo(nuevo) {
    if (nuevo === tipo) return;
    setTipo(nuevo); setFiltros({ ...FILTROS_INIT });
  }
  function setF(campo, val) { setFiltros((p) => ({ ...p, [campo]: val })); setPage(1); }
  function setRango(ini, fin) { setFiltros((p) => ({ ...p, fechaInicio: ini, fechaFinal: fin })); setPage(1); }

  // Período rápido activo (para resaltar el botón)
  const [m0i, m0f] = rangoMes(0);
  const [m1i, m1f] = rangoMes(-1);
  const [aI, aF] = rangoAnio();
  const periodoActivo =
    filtros.fechaInicio === m0i && filtros.fechaFinal === m0f ? "mes" :
    filtros.fechaInicio === m1i && filtros.fechaFinal === m1f ? "mesPasado" :
    filtros.fechaInicio === aI && filtros.fechaFinal === aF ? "anio" : "";

  const kpis = data?.kpis ?? {};
  const kpisPrev = data?.kpisPrevios ?? null;
  const periodoPrevio = data?.periodoPrevio ?? null;
  const porEntidad = data?.porEntidad ?? [];
  const porRubro = data?.porRubro ?? [];
  const evolucion = data?.evolucion ?? [];
  const detalle = data?.detalle ?? [];
  const opcEntidades = data?.opciones?.entidades ?? [];
  const opcRubros = data?.opciones?.rubros ?? [];

  const filtrosActivos = useMemo(() =>
    (filtros.entidad ? 1 : 0) + (filtros.rubro ? 1 : 0) +
    (filtros.fechaInicio ? 1 : 0) + (filtros.fechaFinal ? 1 : 0) +
    (filtros.estado !== "todos" ? 1 : 0) + (filtros.moneda !== "todos" ? 1 : 0),
    [filtros]);

  const totalPages = Math.max(1, Math.ceil(detalle.length / PAGE_SIZE));
  const detallePage = detalle.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Ranking de DEUDA: solo saldos positivos. Un cliente con saldo a favor
  // (negativo) no es deuda — graficarlo corría el eje a negativo y encimaba
  // la barra con su etiqueta. El saldo a favor ya se ve en el KPI y la tabla.
  const entChart = porEntidad
    .filter((e) => Number(e.saldo) > 0.5)
    .slice(0, 12)
    .map((e) => ({
      nombre: e.nombre, saldo: conv(e.saldo), facturado: conv(e.facturado), cobrado: conv(e.cobrado),
    }));
  // Todos los rubros con color propio (hasta 8 — el máximo de hues
  // distinguibles de la paleta); solo si hubiera más de 8, el excedente se
  // agrupa en "Otros" gris.
  const rubroTop = porRubro.slice(0, RUBRO_MAX).map((r) => ({ nombre: r.nombre, value: conv(r.total), porcentaje: r.porcentaje }));
  const rubroResto = porRubro.slice(RUBRO_MAX);
  const rubroOtros = rubroResto.length > 0
    ? [{
        nombre: t("informes.otrosRubros", { n: rubroResto.length }),
        value: conv(rubroResto.reduce((s, r) => s + r.total, 0)),
        porcentaje: Math.round(rubroResto.reduce((s, r) => s + r.porcentaje, 0) * 10) / 10,
        esOtros: true,
      }]
    : [];
  const rubroChart = [...rubroTop, ...rubroOtros];
  const rubroTotal = rubroChart.reduce((s, r) => s + r.value, 0);

  const granularidad = data?.evolucionGranularidad ?? "mensual";
  const evoChart = evolucion.map((m) => ({
    periodo: periodoLabel(m.periodo, granularidad), facturado: conv(m.facturado), cobrado: conv(m.cobrado),
  }));

  const lblEntidad = esCxc ? t("informes.cliente") : t("informes.proveedor");
  const lblCobrado = esCxc ? t("informes.cobrado") : t("informes.pagado");

  // Dona de rubros: al pasar el mouse sobre una porción, el texto se dibuja
  // DENTRO del SVG (centrado en cx/cy), no en un tooltip flotante — así nunca
  // puede quedar tapado ni superpuesto con nada. Mismo patrón que el donut
  // CxC/CxP del Dashboard principal (CxcCxpDonut).
  function renderRubroActiveShape(props) {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
    const nombreCorto = payload.nombre.length > 18 ? `${payload.nombre.slice(0, 17)}…` : payload.nombre;
    return (
      <g>
        <text x={cx} y={cy - 9} textAnchor="middle" fill="var(--text-muted)"
          style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.3px" }}>
          {nombreCorto}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill="var(--text-primary)"
          style={{ fontSize: "1.05rem", fontWeight: 800, fontFamily: "monospace" }}>
          {fmtAxis(payload.value)}
        </text>
        <text x={cx} y={cy + 28} textAnchor="middle" fill={fill}
          style={{ fontSize: "0.7rem", fontWeight: 700 }}>
          {payload.porcentaje}%
        </text>
        <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 4}
          startAngle={startAngle} endAngle={endAngle} fill={fill} />
      </g>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>{t("common.inicio")} /</span></Link>
        <span className={invoiceStyles.active}> {t("informes.titulo")}</span>
      </span>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("informes.titulo")}</h2>
          <p className="mb-0" style={{ color: "var(--text-muted)" }}>{t("informes.subtitulo")}</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <div style={{ display: "flex", background: "var(--bg)", border: "1px solid var(--border-card)", borderRadius: 9, padding: 3 }}>
            {[["cxc", t("informes.cxc")], ["cxp", t("informes.cxp")]].map(([v, lbl]) => (
              <button key={v} type="button" onClick={() => cambiarTipo(v)}
                style={{
                  padding: "7px 16px", borderRadius: 6, border: "none",
                  background: tipo === v ? "linear-gradient(135deg,#f97316,#fb923c)" : "transparent",
                  color: tipo === v ? "#fff" : "var(--text-muted)",
                  boxShadow: tipo === v ? "0 2px 8px rgba(249,115,22,0.35)" : "none",
                  fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", transition: "all 0.18s",
                }}>{lbl}</button>
            ))}
          </div>
          {multimoneda && <CurrencyToggle value={vista} onChange={setVista} title={t("informes.monedaVisualizacion")} />}
        </div>
      </div>

      {/* ── Barra de período rápido + comparación ─────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
        background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12,
        padding: "10px 14px", marginBottom: 14,
      }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <PeriodBtn active={periodoActivo === "mes"} onClick={() => setRango(m0i, m0f)}>{t("informes.esteMes")}</PeriodBtn>
          <PeriodBtn active={periodoActivo === "mesPasado"} onClick={() => setRango(m1i, m1f)}>{t("informes.mesPasado")}</PeriodBtn>
          <PeriodBtn active={periodoActivo === "anio"} onClick={() => setRango(aI, aF)}>{t("informes.esteAnio")}</PeriodBtn>
          <PeriodBtn active={!filtros.fechaInicio && !filtros.fechaFinal} onClick={() => setRango("", "")}>{t("informes.todoTiempo")}</PeriodBtn>
        </div>
        <span style={{ fontSize: "0.76rem", color: "var(--text-muted)", fontStyle: "italic" }}>
          {periodoPrevio
            ? t("informes.comparadoCon", { desde: fmtFecha(periodoPrevio.desde), hasta: fmtFecha(periodoPrevio.hasta) })
            : t("informes.sinPeriodoAnterior")}
        </span>
      </div>

      {/* ── Panel de filtros ─────────────────────────────────────────────── */}
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--text-muted)" }}>
            <i className="fa-solid fa-filter" style={{ marginRight: 7, color: "#f97316" }} />{t("informes.filtros")}
            {filtrosActivos > 0 && <span style={{ marginLeft: 8, background: "#f97316", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: "0.7rem" }}>{filtrosActivos}</span>}
          </span>
          {filtrosActivos > 0 && (
            <button type="button" onClick={() => { setFiltros({ ...FILTROS_INIT }); setPage(1); }}
              style={{ background: "transparent", border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-muted)", cursor: "pointer", fontSize: "0.76rem", padding: "4px 10px" }}>
              <i className="fa-solid fa-broom" style={{ marginRight: 5 }} />{t("informes.limpiar")}
            </button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div>
            <label style={filtroLabel}>{lblEntidad}</label>
            <SearchSelect icon={esCxc ? "fa-user" : "fa-building"}
              options={[{ id: "", primary: t("informes.todos") }, ...opcEntidades.map((e) => ({ id: String(e.id), primary: e.nombre }))]}
              value={filtros.entidad || ""} onChange={(id) => setF("entidad", id)} placeholder={t("informes.todos")} />
          </div>
          <div>
            <label style={filtroLabel}>{t("informes.rubro")}</label>
            <SearchSelect icon="fa-tags"
              options={[{ id: "", primary: t("informes.todos") }, ...opcRubros.map((r) => ({ id: String(r.id), primary: r.codigo || r.nombre, secondary: r.nombre }))]}
              value={filtros.rubro || ""} onChange={(id) => setF("rubro", id)} placeholder={t("informes.todos")} />
          </div>
          <div>
            <label style={filtroLabel}>{t("informes.desde")}</label>
            <input type="date" className="form-control" value={filtros.fechaInicio} onChange={(e) => setF("fechaInicio", e.target.value)} style={inputSt} />
          </div>
          <div>
            <label style={filtroLabel}>{t("informes.hasta")}</label>
            <input type="date" className="form-control" value={filtros.fechaFinal} onChange={(e) => setF("fechaFinal", e.target.value)} style={inputSt} />
          </div>
          <div>
            <label style={filtroLabel}>{t("informes.estado")}</label>
            <select className="form-select" value={filtros.estado} onChange={(e) => setF("estado", e.target.value)} style={inputSt}>
              <option value="todos">{t("informes.todos")}</option>
              <option value="pendiente">{t("informes.pendiente")}</option>
              <option value="parcial">{t("informes.parcial")}</option>
              <option value="pagado">{t("informes.pagadoEstado")}</option>
            </select>
          </div>
          {multimoneda && (
            <div>
              <label style={filtroLabel}>{t("informes.monedaFactura")}</label>
              <select className="form-select" value={filtros.moneda} onChange={(e) => setF("moneda", e.target.value)} style={inputSt}>
                <option value="todos">{t("informes.todos")}</option>
                <option value="CRC">CRC (₡)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--text-secondary)" }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#f97316", fontSize: "1.6rem", display: "block", marginBottom: 10 }} />
          {t("informes.cargando")}
        </div>
      ) : (
        <>
          {/* ── KPIs con delta ────────────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 16 }}>
            <Kpi label={t("informes.totalFacturado")} value={fmt(kpis.totalFacturado)} icon="fa-file-invoice-dollar" color="#60a5fa"
              delta={delta(kpis.totalFacturado, kpisPrev?.totalFacturado)} goodUp t={t} />
            <Kpi label={lblCobrado} value={fmt(kpis.totalCobrado)} icon="fa-circle-check" color="#4ade80"
              delta={delta(kpis.totalCobrado, kpisPrev?.totalCobrado)} goodUp t={t} />
            <Kpi label={t("informes.totalPendiente")} value={fmt(kpis.totalPendiente)} icon="fa-hourglass-half" color="#f87171"
              delta={delta(kpis.totalPendiente, kpisPrev?.totalPendiente)} goodUp={false} t={t} />
            <Kpi label={t("informes.numDocumentos")} value={String(kpis.numDocumentos ?? 0)} icon="fa-file-lines" color="#c084fc" isCount
              delta={delta(kpis.numDocumentos, kpisPrev?.numDocumentos)} goodUp t={t} />
          </div>

          {/* ── Evolución (área) + Rubro (dona) ───────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 16, marginBottom: 16, alignItems: "start" }}>
            <Card>
              <CardTitle icon="fa-chart-area" color="#4ade80" title={esCxc ? t("informes.evolucionCxc") : t("informes.evolucionCxp")}
                right={evoChart.length > 0 && (
                  <span style={{ display: "inline-flex", gap: 14, fontSize: "0.72rem", fontVariantNumeric: "tabular-nums" }}>
                    <span style={{ color: "#60a5fa", fontWeight: 700, fontFamily: "monospace" }}>
                      {fmtAxis(evoChart.reduce((s, m) => s + m.facturado, 0))}
                    </span>
                    <span style={{ color: "#4ade80", fontWeight: 700, fontFamily: "monospace" }}>
                      {fmtAxis(evoChart.reduce((s, m) => s + m.cobrado, 0))}
                    </span>
                  </span>
                )} />
              {evoChart.length === 0 ? <Vacio t={t} /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={evoChart} margin={{ top: 10, right: 10, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gFact" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gCob" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4ade80" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#4ade80" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="none" stroke="var(--border-inner)" vertical={false} />
                    <XAxis dataKey="periodo" tick={{ fontSize: 11, fill: "var(--text-muted)" }} axisLine={{ stroke: "var(--border-inner)" }} tickLine={false} />
                    <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} width={52} />
                    <Tooltip content={(p) => <ChartTip {...p} fmt={fmtDisp} />}
                      cursor={{ stroke: "var(--text-muted)", strokeWidth: 1, strokeDasharray: "3 3" }} />
                    <Area type="monotone" dataKey="facturado" name={t("informes.facturado")} stroke="#60a5fa" strokeWidth={2.4}
                      fill="url(#gFact)" dot={false}
                      activeDot={{ r: 5, fill: "#60a5fa", stroke: "var(--bg-card)", strokeWidth: 2 }} />
                    <Area type="monotone" dataKey="cobrado" name={lblCobrado} stroke="#4ade80" strokeWidth={2.4}
                      fill="url(#gCob)" dot={false}
                      activeDot={{ r: 5, fill: "#4ade80", stroke: "var(--bg-card)", strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              {/* leyenda manual */}
              {evoChart.length > 0 && (
                <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 6 }}>
                  <LegendDot color="#60a5fa" label={t("informes.facturado")} />
                  <LegendDot color="#4ade80" label={lblCobrado} />
                </div>
              )}
            </Card>

            <Card>
              <CardTitle icon="fa-chart-pie" color="#c084fc" title={t("informes.distribucionRubro")} />
              {rubroChart.length === 0 ? <Vacio t={t} /> : (
                <div>
                  <div style={{ position: "relative" }}>
                    <ResponsiveContainer width="100%" height={224}>
                      <PieChart>
                        <Pie data={rubroChart} dataKey="value" nameKey="nombre" cx="50%" cy="50%"
                          innerRadius={62} outerRadius={94} paddingAngle={2.5} cornerRadius={5}
                          stroke="var(--bg-card)" strokeWidth={2}
                          activeIndex={rubroActivo ?? undefined}
                          activeShape={rubroActivo != null ? renderRubroActiveShape : undefined}
                          onMouseEnter={(_, i) => setRubroActivo(i)}
                          onMouseLeave={() => setRubroActivo(null)}>
                          {rubroChart.map((r, i) => (
                            <Cell key={i}
                              fill={r.esOtros ? rubroOtrosColor : rubroColors[i % rubroColors.length]}
                              opacity={rubroActivo == null || rubroActivo === i ? 1 : 0.35}
                              style={{ transition: "opacity 0.18s", cursor: "pointer" }} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Total al centro — el texto de la porción con hover se dibuja
                        DENTRO del SVG (renderRubroActiveShape), así que este total
                        solo se muestra cuando no hay ninguna porción activa; nunca
                        se superponen. */}
                    {rubroActivo == null && (
                      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                        <span style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", color: "var(--text-muted)" }}>{t("informes.total")}</span>
                        {/* Compacto (₡10.4M): el monto completo no cabe en el agujero
                            de la dona — la cifra exacta está en el KPI y la leyenda. */}
                        <span style={{ fontSize: "1.35rem", fontWeight: 800, fontFamily: "monospace", color: "var(--text-primary)", lineHeight: 1.1 }}>{fmtAxis(rubroTotal)}</span>
                        <span style={{ fontSize: "0.66rem", color: "var(--text-muted)", marginTop: 3 }}>
                          {rubroChart.length} {t("informes.rubros")}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Leyenda enriquecida: cada rubro con su barra de proporción,
                      sincronizada con el hover de la dona (y viceversa). */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 160, overflowY: "auto", marginTop: 10, paddingRight: 4,
                    scrollbarWidth: "thin", scrollbarColor: "var(--border-card) transparent" }}>
                    {rubroChart.map((r, i) => {
                      const color = r.esOtros ? rubroOtrosColor : rubroColors[i % rubroColors.length];
                      const activo = rubroActivo === i;
                      return (
                        <div key={i}
                          onMouseEnter={() => setRubroActivo(i)}
                          onMouseLeave={() => setRubroActivo(null)}
                          style={{
                            padding: "7px 10px", borderRadius: 8, cursor: "default",
                            background: activo ? "var(--hover-bg)" : "transparent",
                            transition: "background 0.15s",
                          }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.78rem", marginBottom: 5 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                            <span style={{ color: activo ? "var(--text-primary)" : "var(--text-secondary)", fontWeight: activo ? 700 : 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.nombre}</span>
                            <span style={{ color: "var(--text-muted)", fontFamily: "monospace", fontSize: "0.73rem" }}>{fmtDisp(r.value)}</span>
                            <span style={{ color: "var(--text-primary)", fontWeight: 700, minWidth: 44, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.porcentaje}%</span>
                          </div>
                          {/* Barra de proporción */}
                          <div style={{ height: 5, borderRadius: 3, background: "var(--hover-bg)", overflow: "hidden" }}>
                            <div style={{
                              height: "100%", borderRadius: 3, background: color,
                              width: `${Math.max(1.5, Math.min(100, r.porcentaje))}%`,
                              opacity: rubroActivo == null || activo ? 1 : 0.35,
                              transition: "opacity 0.18s",
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* ── Deuda por entidad ─────────────────────────────────────────── */}
          <Card style={{ marginBottom: 16 }}>
            <CardTitle icon="fa-ranking-star" color="#f87171" title={esCxc ? t("informes.deudaPorCliente") : t("informes.deudaPorProveedor")} />
            {entChart.length === 0 ? <Vacio t={t} /> : (
              <ResponsiveContainer width="100%" height={Math.max(200, entChart.length * 36 + 20)}>
                <BarChart data={entChart} layout="vertical" margin={{ top: 4, right: 68, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gSaldo" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#fb7185" />
                      <stop offset="100%" stopColor="#f87171" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="none" stroke="var(--border-inner)" horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 10, fill: "var(--text-muted)" }} axisLine={{ stroke: "var(--border-inner)" }} tickLine={false} />
                  <YAxis type="category" dataKey="nombre" width={130}
                    tickFormatter={(n) => (n.length > 16 ? `${n.slice(0, 15)}…` : n)}
                    tick={{ fontSize: 11, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} />
                  <Tooltip content={(p) => <ChartTip {...p} fmt={fmtDisp} />} cursor={{ fill: "rgba(248,113,113,0.06)" }} />
                  <Bar dataKey="saldo" name={t("informes.saldoPendiente")} fill="url(#gSaldo)" radius={[0, 4, 4, 0]} maxBarSize={20}>
                    <LabelList dataKey="saldo" position="right" formatter={fmtDisp}
                      style={{ fontSize: "0.72rem", fontWeight: 700, fill: "var(--text-primary)", fontFamily: "monospace" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* ── Tabla detalle ─────────────────────────────────────────────── */}
          <div className={invoiceStyles["table-container"]}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-card)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--text-primary)" }}>
                <i className="fa-solid fa-table-list" style={{ marginRight: 8, color: "#f97316" }} />{t("informes.detalle")}
              </span>
              <span style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>
                {data?.detalleTotal ?? detalle.length} {t("informes.documentos")}
                {(data?.detalleTotal ?? 0) > detalle.length && ` · ${t("informes.mostrando", { n: detalle.length })}`}
              </span>
            </div>
            <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
              <table>
                <thead>
                  <tr>
                    <th>{t("informes.fecha")}</th><th>{t("informes.tipo")}</th><th>{t("informes.documento")}</th><th>{lblEntidad}</th><th>{t("informes.rubro")}</th>
                    <th className="text-end">{t("informes.monto")}</th><th className="text-end">{t("informes.saldo")}</th><th className="text-center">{t("informes.estado")}</th>
                  </tr>
                </thead>
                <tbody>
                  {detallePage.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                      <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }} />{t("informes.sinResultados")}
                    </td></tr>
                  ) : detallePage.map((d) => (
                    <tr key={`${d.tipo}-${d.id}`}>
                      <td style={{ color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{fmtFecha(d.fecha)}</td>
                      <td><TipoDocBadge tipo={d.tipo} t={t} /></td>
                      <td style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{d.numero}</td>
                      <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{d.entidad}</td>
                      <td style={{ color: "var(--text-secondary)", fontSize: "0.82rem" }}>{d.rubro ?? "—"}</td>
                      <td className="text-end" style={{ fontFamily: "monospace" }}>{fmt(d.monto)}</td>
                      <td className="text-end" style={{ fontFamily: "monospace", fontWeight: 600, color: d.saldo == null ? "var(--text-muted)" : d.saldo > 0.5 ? "#f87171" : "#4ade80" }}>
                        {d.saldo == null ? "—" : fmt(Math.abs(d.saldo))}
                      </td>
                      <td className="text-center"><EstadoBadge estado={d.estado} t={t} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={invoiceStyles["table-footer"]}>
              <p><span>{detalle.length}</span> {t("informes.documentos")} · {t("common.pagina")} <span>{page}</span> {t("common.de")} <span>{totalPages}</span></p>
              <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>{t("common.anterior")}</button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>{t("common.siguiente")}</button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ─── delta helper ─────────────────────────────────────────────────────────── */
function delta(actual, previo) {
  if (previo == null) return null;                    // sin período anterior
  const a = Number(actual) || 0, p = Number(previo) || 0;
  if (p === 0) return a === 0 ? { pct: 0, nuevo: false } : { pct: null, nuevo: true };
  return { pct: ((a - p) / Math.abs(p)) * 100, nuevo: false };
}

/* ─── estilos ─────────────────────────────────────────────────────────────── */
const filtroLabel = { display: "block", fontSize: "0.72rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.3px" };
const inputSt = { fontSize: "0.85rem" };

function PeriodBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: "5px 13px", borderRadius: 7, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
      border: `1px solid ${active ? "#f97316" : "var(--border-card)"}`,
      background: active ? "rgba(249,115,22,0.12)" : "transparent",
      color: active ? "#f97316" : "var(--text-secondary)", transition: "all 0.15s",
    }}>{children}</button>
  );
}

/* ─── KPI card ─────────────────────────────────────────────────────────────── */
function Kpi({ label, value, icon, color, delta, goodUp = true, isCount, t }) {
  let chip = null;
  if (delta) {
    if (delta.nuevo) {
      chip = <DeltaChip color="#4ade80" icon="fa-arrow-up" text={t("informes.nuevo")} />;
    } else if (delta.pct != null) {
      const up = delta.pct >= 0;
      const bueno = up === goodUp;
      const c = Math.abs(delta.pct) < 0.05 ? "var(--text-muted)" : (bueno ? "#4ade80" : "#f87171");
      const ic = Math.abs(delta.pct) < 0.05 ? "fa-minus" : (up ? "fa-arrow-up" : "fa-arrow-down");
      chip = <DeltaChip color={c} icon={ic} text={`${Math.abs(delta.pct).toFixed(1)}%`} />;
    }
  }
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 14,
      padding: "16px 18px", position: "relative", overflow: "hidden",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: color, opacity: 0.9 }} />
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: "0.67rem", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</p>
          <p style={{ margin: "6px 0 0", fontFamily: "monospace", fontWeight: 800, fontSize: isCount ? "1.5rem" : "1.25rem", color: "var(--text-primary)", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</p>
          <div style={{ marginTop: 8, minHeight: 18 }}>
            {chip || <span style={{ fontSize: "0.68rem", color: "var(--text-dim)" }}>{t("informes.sinComparacion")}</span>}
          </div>
        </div>
        <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: `${color}18`, border: `1px solid ${color}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className={`fa-solid ${icon}`} style={{ color, fontSize: "1.05rem" }} />
        </div>
      </div>
    </div>
  );
}

function DeltaChip({ color, icon, text }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.72rem", fontWeight: 700, color, background: `${color}14`, borderRadius: 6, padding: "2px 8px" }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: "0.62rem" }} />{text}
    </span>
  );
}

/* ─── Card + title ─────────────────────────────────────────────────────────── */
function Card({ children, style }) {
  return <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 2px rgba(0,0,0,0.04)", ...style }}>{children}</div>;
}
function CardTitle({ icon, color, title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: `${color}16`, border: `1px solid ${color}2e`,
        display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.76rem" }} />
      </span>
      <span style={{ fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.6px", textTransform: "uppercase", color: "var(--text-muted)", flex: 1 }}>{title}</span>
      {right}
    </div>
  );
}
function LegendDot({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.75rem", color: "var(--text-muted)" }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />{label}
    </span>
  );
}
function Vacio({ t }) {
  return (
    <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 13 }}>
      <i className="fa-solid fa-chart-simple" style={{ marginRight: 8, opacity: 0.5 }} />{t("informes.sinDatos")}
    </div>
  );
}
function ChartTip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 8, padding: "10px 14px", fontSize: "0.78rem", boxShadow: "0 6px 24px rgba(0,0,0,0.35)" }}>
      {label && <p style={{ margin: "0 0 6px", fontWeight: 700, color: "var(--text-primary)" }}>{label}</p>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.stroke || p.fill || p.color || p.payload?.fill }} />
          <span style={{ color: "var(--text-muted)", minWidth: 62 }}>{p.name || p.payload?.nombre}:</span>
          <span style={{ color: "var(--text-primary)", fontWeight: 700, fontFamily: "monospace" }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}
function TipoDocBadge({ tipo, t }) {
  const esFactura = tipo === "factura";
  const c = esFactura ? "#60a5fa" : "#4ade80";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 6,
      fontSize: "0.7rem", fontWeight: 700, background: `${c}16`, color: c, border: `1px solid ${c}35`, whiteSpace: "nowrap" }}>
      <i className={`fa-solid ${esFactura ? "fa-file-invoice" : "fa-receipt"}`} style={{ fontSize: "0.6rem" }} />
      {esFactura ? t("informes.tipoFactura") : t("informes.tipoRecibo")}
    </span>
  );
}
function EstadoBadge({ estado, t }) {
  const cfg = {
    pagado:    { c: "#4ade80", bg: "rgba(74,222,128,0.12)", i: "fa-check", label: t("informes.pagadoEstado") },
    parcial:   { c: "#fbbf24", bg: "rgba(251,191,36,0.12)", i: "fa-clock", label: t("informes.parcial") },
    pendiente: { c: "#f87171", bg: "rgba(248,113,113,0.12)", i: "fa-exclamation", label: t("informes.pendiente") },
  }[estado] || { c: "#94a3b8", bg: "rgba(148,163,184,0.12)", i: "fa-minus", label: estado };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 600, background: cfg.bg, color: cfg.c, border: `1px solid ${cfg.c}40` }}>
      <i className={`fa-solid ${cfg.i}`} style={{ fontSize: "0.62rem" }} />{cfg.label}
    </span>
  );
}
